import { useCallback, useEffect, useRef, useState } from "react";

import { sameSearchCell } from "./supplierFilters";
import {
  FIRST_FIX_GRACE_MS,
  FIX_JITTER_METERS,
  coordinateLabel,
  isPermissionDenied,
  metersBetween,
  readFix,
  readPermission,
  requestFix,
  reverseGeocode,
  watchPermission,
  writeFix,
} from "./locationFix";

const seedState = (initialCoordinates, initialAddress) => {
  if (initialCoordinates) {
    return {
      coordinates: initialCoordinates,
      address: initialAddress || "",
      shortLabel: initialAddress || coordinateLabel(initialCoordinates),
      locating: false,
      failed: false,
      fromCache: false,
    };
  }

  const cached = readFix();

  if (cached) {
    return {
      coordinates: cached.coordinates,
      address: cached.address || "",
      shortLabel: cached.shortLabel || coordinateLabel(cached.coordinates),
      locating: false,
      failed: false,
      fromCache: true,
    };
  }

  return {
    coordinates: null,
    address: "",
    shortLabel: "",
    locating: true,
    failed: false,
    fromCache: false,
  };
};

const useMapLocation = ({
  initialCoordinates = null,
  initialAddress = "",
  mapsReady = false,
} = {}) => {
  const [state, setState] = useState(() =>
    seedState(initialCoordinates, initialAddress)
  );

  const [permission, setPermission] = useState("unknown");
  const [refitToken, setRefitToken] = useState(0);
  const [graceElapsed, setGraceElapsed] = useState(false);

  const manualRef = useRef(Boolean(initialCoordinates));
  const coordinatesRef = useRef(state.coordinates);
  const labelledRef = useRef(null);

  coordinatesRef.current = state.coordinates;

  const commitFix = useCallback((coordinates, { manual = false, refit = false } = {}) => {
    if (!manual && manualRef.current) return;

    setState((current) => {
      const unchanged =
        sameSearchCell(current.coordinates, coordinates) ||
        (!manual && metersBetween(current.coordinates, coordinates) < FIX_JITTER_METERS);

      if (unchanged) {
        return current.locating || current.failed
          ? { ...current, locating: false, failed: false }
          : current;
      }

      return {
        ...current,
        coordinates,
        locating: false,
        failed: false,
      };
    });

    if (refit) setRefitToken((token) => token + 1);
  }, []);

  const resolveLabel = useCallback(async (coordinates) => {
    const cellKey = `${coordinates.latitude.toFixed(3)},${coordinates.longitude.toFixed(3)}`;
    if (labelledRef.current === cellKey) return;

    labelledRef.current = cellKey;

    const resolved = await reverseGeocode(coordinates);

    const address = resolved?.address || coordinateLabel(coordinates);
    const shortLabel = resolved?.shortLabel || coordinateLabel(coordinates);

    setState((current) => {
      if (!sameSearchCell(current.coordinates, coordinates)) return current;

      return { ...current, address, shortLabel };
    });

    writeFix({ coordinates, address, shortLabel, at: Date.now() });
  }, []);

  const runFix = useCallback(
    async ({ refit = false, showSpinner = false } = {}) => {
      if (showSpinner) setState((current) => ({ ...current, locating: true }));

      try {
        const coordinates = await requestFix();

        manualRef.current = false;
        commitFix(coordinates, { refit });

        return coordinates;
      } catch (error) {
        if (isPermissionDenied(error)) setPermission("denied");

        setState((current) =>
          current.coordinates
            ? { ...current, locating: false }
            : { ...current, locating: false, failed: true }
        );

        return null;
      }
    },
    [commitFix]
  );

  useEffect(() => {
    let cancelled = false;

    readPermission().then((value) => {
      if (cancelled) return;

      setPermission(value);

      if (manualRef.current) return;

      if (value === "denied") {
        setState((current) => ({ ...current, locating: false }));
        return;
      }

      runFix({ showSpinner: !coordinatesRef.current });
    });

    return () => {
      cancelled = true;
    };
  }, [runFix]);

  useEffect(
    () =>
      watchPermission((value) => {
        setPermission(value);

        if (value === "granted" && !manualRef.current) {
          runFix({ refit: true, showSpinner: !coordinatesRef.current });
        }
      }),
    [runFix]
  );

  useEffect(() => {
    if (state.coordinates || graceElapsed) return undefined;

    let timer = 0;

    const arm = () => {
      timer = window.setTimeout(() => {
        if (document.visibilityState === "hidden") {
          arm();
          return;
        }

        setGraceElapsed(true);
      }, FIRST_FIX_GRACE_MS);
    };

    arm();

    return () => window.clearTimeout(timer);
  }, [state.coordinates, graceElapsed]);

  useEffect(() => {
    if (!mapsReady || !state.coordinates) return;

    resolveLabel(state.coordinates);
  }, [mapsReady, state.coordinates, resolveLabel]);

  const locate = useCallback(() => {
    manualRef.current = false;
    labelledRef.current = null;
    setRefitToken((token) => token + 1);

    return runFix({ refit: false, showSpinner: !coordinatesRef.current });
  }, [runFix]);

  const selectPlace = useCallback(
    (coordinates, address) => {
      manualRef.current = true;
      labelledRef.current = `${coordinates.latitude.toFixed(3)},${coordinates.longitude.toFixed(3)}`;

      setState({
        coordinates,
        address: address || coordinateLabel(coordinates),
        shortLabel: address || coordinateLabel(coordinates),
        locating: false,
        failed: false,
        fromCache: false,
      });

      setRefitToken((token) => token + 1);
    },
    []
  );

  return {
    coordinates: state.coordinates,
    address: state.address,
    locationLabel: state.shortLabel,
    locating: state.locating,
    failed: state.failed,
    permission,
    canPrompt: permission !== "granted" && permission !== "denied",
    graceElapsed,
    refitToken,
    locate,
    selectPlace,
  };
};

export default useMapLocation;
